import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ContentSection,
  ErrorMessage,
  Fieldset,
  FormField,
  Heading,
  Input,
  Layout,
  Paragraph,
  Spinner,
} from '@vwfs-bronson/bronson-react';
import { getCreditApplicationResponse } from '../../services/api/miles/milesIdProvider';
import { sendTanToNumber, validateTan } from '../../services/api/sendTan/sendTan';
import { getTransactionId } from '../../services/common/data';
import { store } from '../../services/redux';
import { setCreditApplicationDetails } from '../../services/redux/features/creditApplication.slice';
import { DataStorageUpdateResponse, setDataStorageNew } from '../../services/redux/features/dataStorage.redux';
import { loadingActions, loadingSelectors } from '../../services/redux/features/loading.slice';
import { useNavigate } from '../../services/routing/navigate';

/**
 * @deprecated Do not use. Will be removed as soon as all references are gone.
 */
export const SpinnerSection = () => {
  return (
    <ContentSection pageWrap className="u-m-xlarge">
      <Spinner center fullPage />
    </ContentSection>
  );
};

export const AuthenticatePage = () => {
  const { t } = useTranslation();
  const [tan, setTan] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const dataStorageId = getTransactionId();

  const navigate = useNavigate();
  const isLoading = useSelector(loadingSelectors.selectLoading);
  const dispatch = useDispatch();

  useEffect(() => {
    if (dataStorageId) {
      sendTanToNumber(dataStorageId);
    }
  }, [dataStorageId, dispatch]);

  const onSendTanButtonClick = async () => {
    try {
      await sendTanToNumber(dataStorageId);
    } catch {
      setError(true);
    }
  };

  const onValidateTanButtonClick = async () => {
    let dataStorageObject = {} as { data: DataStorageUpdateResponse };
    store.dispatch(loadingActions.setLoading(true));

    try {
      dataStorageObject = await validateTan({ tan });
    } catch {
      navigate.error();
      store.dispatch(loadingActions.setLoading(false));
      return;
    }

    dispatch(setDataStorageNew(dataStorageObject.data.custom));

    if (dataStorageObject.data.custom.creditApplicationId) {
      const creditApplicationResponse = await getCreditApplicationResponse(
        dataStorageObject.data.custom.creditApplicationId
      );

      if (creditApplicationResponse) {
        store.dispatch(setCreditApplicationDetails(creditApplicationResponse));
      }
    }

    const { lastVisitedLocation } = dataStorageObject.data.custom;

    if (lastVisitedLocation) {
      navigate.url(`${lastVisitedLocation}?cid=${dataStorageId}`);
    } else {
      navigate.error();
    }
    store.dispatch(loadingActions.setLoading(false));
  };

  return (
    <Fieldset>
      <Fieldset.Row>
        <Layout className="u-text-center">
          <Layout.Item default="1/1">
            <Layout center>
              <Heading level={2}>{t('authenticate:title')}</Heading>
            </Layout>
          </Layout.Item>
          <Layout.Item default="1/1" className="u-ml@s">
            <Layout center>
              <Paragraph>{t('authenticate:paragraph')}</Paragraph>
            </Layout>
          </Layout.Item>
          <Layout.Item default="1/1">
            {error && <ErrorMessage>{t('authenticate:errorMessage')}</ErrorMessage>}
            <Layout center>
              <FormField type="input">
                <Input
                  placeholder={t('authenticate:pin')}
                  value={tan}
                  onChange={(e) => {
                    setError(false);
                    setTan(e.target.value);
                  }}
                />
              </FormField>
            </Layout>
          </Layout.Item>
          <Layout.Item default="1/1">
            <Layout center>
              {!isLoading && (
                <Button
                  type="button"
                  data-tr-component="Button Link"
                  data-tr-function="Continue"
                  icon=""
                  link
                  onClick={onSendTanButtonClick}
                  secondary
                >
                  {t('authenticate:newTanCode')}
                </Button>
              )}
            </Layout>
          </Layout.Item>

          <Layout.Item default="1/1">
            <Layout center className="u-mb@s">
              <Button onClick={onValidateTanButtonClick}>{t('authenticate:submit')}</Button>
            </Layout>
          </Layout.Item>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
